import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '@/lib/api'
import { roleLabel } from '@/lib/navigation'
import { queryKeys } from '@/lib/query'
import { MOCK_ROLE_NAMES } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Role, RoleNameEntry } from '@/types/domain'

/* Every role's display name, for every signed-in reader (story B3).
 *
 * A role created on the roles screen has no catalogue word, and the team DTOs
 * carry a member's role KEY only — so every team card used to print
 * `ops-lead` where the founder had typed "Khorgos desk lead". The name was
 * served only by /admin/roles, behind admin.roles.manage, which every Sales
 * user is refused. GET /api/roles answers any session with key and name and
 * nothing else (identity.RoleName in dto.go).
 *
 * A mock branch, unlike useRoles beside it: that file declines one because a
 * mocked role list is a mocked ACCESS MATRIX. A list of names grants nothing.
 */
export function useRoleNames(
  /** For a sheet most readers never open, as useColleagues takes it. Omitted
   *  means enabled. */
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery<RoleNameEntry[]>({
    queryKey: queryKeys.roleNames(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_ROLE_NAMES
        : (await api.get<{ items: RoleNameEntry[] }>('/roles')).items,
    /* A role is renamed on the roles screen, which invalidates this key in the
       same tab (useRoles' three mutations). Another tab or another founder's
       rename is the only staleness left, and a role's name is not something a
       reader is waiting on — half an hour rather than a refetch per page. */
    staleTime: 30 * 60 * 1000,
  })
}

/** A function naming a role as the reader sees it, for a plain string — a
 *  picker's hint, a `title`. `<RoleName>` is the JSX form of the same rule.
 *
 *  `roleLabel` decides: a seeded role is the catalogue word for its key (QW-1
 *  rule a — «Продажи» whatever English the row stores), any other the name GET
 *  /api/roles serves, and a key the list does not know (created after it was
 *  read, or the read refused) is printed as the key. */
export function useRoleLabel({ enabled }: { enabled?: boolean } = {}): (role: Role) => string {
  const { t } = useTranslation()
  const { data } = useRoleNames({ enabled })

  const served = useMemo(
    () => new Map((data ?? []).map((entry) => [entry.key, entry.name])),
    [data],
  )

  return useCallback(
    (role: Role) => {
      const label = roleLabel(role, served.get(role))
      return label.translated ? t(label.key) : label.text
    },
    [served, t],
  )
}
